import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, Avatar } from '@mui/material';
import EndStoryDialog from './EndStoryDialog';
import { TextToSpeechService } from "../services/TextToSpeechService";
import { IconButton, Snackbar, Tooltip, Button, Slider } from '@mui/material';
import { PlayArrow, VolumeUp, VolumeMute, RecordVoiceOver, AutoMode, ArrowBack, Forward, Feedback  } from '@mui/icons-material';
import AnalyticsService from '../services/AnalyticsService';

const StoryPlayback = ({ storyId, story, onReplay, onNewStory, onBackPress, assets }) => {

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isEndDialogOpen, setIsEndDialogOpen] = useState(false);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);

  const [dialogueClicked, setDialogueClicked] = useState(false);

  const [autoplay, setAutoplay] = useState(() => {
    return JSON.parse(localStorage.getItem("autoplay")) ?? true;
  });

  const DIALOGUE_TRANSITION_DURATION = 300; // milliseconds
  const [isTransitioning, setIsTransitioning] = useState(false);

  const [revealCompleted, setRevealCompleted] = useState(false);

  const { character: characterName, text } = story.dialogue[currentIndex];
  const words = text.split(' ');

  // Find the character object from the story's characters array
  const character = story.characters.find((c) => c.name === characterName);
  // Reference to the audio element
  const audioRef = useRef(null);
  const ttsService = useRef(new TextToSpeechService(setCurrentWordIndex));
  const defaultBGVolume = 0.15;

  const [bgmVolume, setBgmVolume] = useState(() => {
    return JSON.parse(localStorage.getItem("bgmVolume")) ?? defaultBGVolume;
  });

  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [isInteracting, setIsInteracting] = useState(false); // Tracks user interaction

  const [useTTS, setUseTTS] = useState(() => {
    return JSON.parse(localStorage.getItem("useTTS")) ?? false;
  });

  const [useTTSWarning, setUseTTSWarning] = useState(true);

  const [showIntro, setShowIntro] = useState(true);
  const [showOptions, setShowOptions] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', key: 0 });

  const imageCache = useRef(new Map());
  const characterImage = imageCache.current.get(story.dialogue[currentIndex]?.character) || assets.characters[story.dialogue[currentIndex]?.character];

  const isNarratorDialogue = characterName.toLowerCase() === "narrator";

  const [isPaused, setIsPaused] = useState(false);
  const [appBuildId, setAppBuildId] = useState('');

  const updateTTS = (val) => {
    setUseTTS(val);
    localStorage.setItem("useTTS", JSON.stringify(val)); // ✅ Save to localStorage
    AnalyticsService.trackEvent("Playback", "TTS", useTTS ? "Disabled" : "Enabled");
  };

  const updateAutoplay = (val) => {
    setAutoplay(val);
    localStorage.setItem("autoplay", JSON.stringify(val)); // ✅ Save to localStorage
    AnalyticsService.trackEvent("Playback", "Autoplay", autoplay ? "Disabled" : "Enabled");
  };

  const handleBgmVolumeChange = (newVolume) => {
    setBgmVolume(newVolume);
    localStorage.setItem("bgmVolume", JSON.stringify(newVolume)); // ✅ Save to localStorage
  };

  useEffect(() => {
    const styleSheet = document.styleSheets[0];
    styleSheet.insertRule(`
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
    `, styleSheet.cssRules.length);
  }, []);
  
  useEffect(() => {
    AnalyticsService.trackPageView(`/play/${storyId}`, `Loaded ${story.title}`);
  }, [storyId, story.title]);

  useEffect(() => {
    const preloadImages = async () => {
      const promises = story.characters.map((character) => {
        if (character.name && assets.characters[character.name]) {
          return new Promise((resolve) => {
            const img = new Image();
            img.src = assets.characters[character.name];
            img.onload = () => {
              imageCache.current.set(character.name, img.src);
              resolve(); // ✅ Resolve when the image is loaded
            };
          });
        }
        return null;
      });

      await Promise.all(promises);
    };

    preloadImages(); // ✅ Preload images concurrently
  }, [story.characters, assets.characters]);


  useEffect(() => {
    setShowOptions(false);
    setTimeout(() => {
      setShowOptions(true);
      setShowTooltip(true);
      setTimeout(() => setShowTooltip(false), 3000);
    }, 500); // Hide after 2.5s
  }, []);

  useEffect(() => {
    
    const handleBeforeUnload = () => {
      ttsService.current.stop();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);


  useEffect(() => {
    const audioElement = audioRef.current;
    if (!audioElement || !assets.backgroundMusic) return;

    function init() {
      audioElement.src = assets.backgroundMusic;
      audioElement.loop = true;
    }

    const isIPhone = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (isIPhone) {
      // For iPhones, wait for user interaction before initializing.
      const handleUserInteraction = () => {
        init();
        document.removeEventListener("touchstart", handleUserInteraction);
        document.removeEventListener("click", handleUserInteraction);
      };

      document.addEventListener("touchstart", handleUserInteraction, { once: true });
      document.addEventListener("click", handleUserInteraction, { once: true });
    } else {
      init();
    }
  }, [assets.backgroundMusic]);

  // Effect 2: Update playback based on bgmVolume or isPaused
  useEffect(() => {
    const audioElement = audioRef.current;
    if (!audioElement) return;

    // Always update the volume.
    audioElement.volume = bgmVolume;

    // Use the isPaused flag to determine whether to play or pause.
    if (isPaused || bgmVolume === 0) {
      audioElement.pause();
    } else {
      audioElement.play().catch(error => console.error('Error playing background music:', error));
    }
  }, [bgmVolume, isPaused]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setIsPaused(true); // Pause when tab is hidden
        AnalyticsService.trackEvent('Playback', 'PauseStatus', 'Paused');
      } else {
        setIsPaused(false); // Resume when tab is focused
        AnalyticsService.trackEvent('Playback', 'PauseStatus', 'Resumed');
      }
    };
  
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // Reset word index when dialogue changes
  useEffect(() => {
    setCurrentWordIndex(0);
    setRevealCompleted(false);
    setDialogueClicked(false);
    if(isPaused)
    {
      ttsService.current.stop();
    }
  }, [currentIndex, text, useTTS, isPaused]);

  // Start speech synthesis with a boundary callback for word-by-word syncing
  useEffect(() => {
    if (!text || showIntro || isEndDialogOpen || isPaused) return;

    const ttsServiceInstance = ttsService.current;

    if (useTTS) {
      ttsServiceInstance.startTTS(text, words.length);
    } else {
      const interval = setInterval(() => {
        setCurrentWordIndex((prev) => {
          if (prev < words.length) {
            return prev + 1;
          } else {
            clearInterval(interval);
            return prev;
          }
        });
      }, 300);
      return () => clearInterval(interval);
    }

    return () => ttsServiceInstance.stop();
  }, [text, useTTS, words.length, showIntro, isEndDialogOpen, isPaused]);

  const nextDialogue = useCallback(() => {
    if (currentIndex < story.dialogue.length - 1) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex(prev => prev + 1);
        setIsTransitioning(false);
      }, DIALOGUE_TRANSITION_DURATION); // Match transition duration
    } else {
      setIsEndDialogOpen(true);
      AnalyticsService.trackEvent('Playback', 'Completed Story', storyId);
    }
  }, [currentIndex, story.dialogue.length, storyId]);

  // Handle autoplay to switch to next dialogue
  useEffect(() => {
    if (autoplay && revealCompleted && !showIntro) {
      const timer = setTimeout(() => {
        nextDialogue(); // Advance to next dialogue
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [autoplay, revealCompleted, nextDialogue, showIntro]);

  // Hide slider after 3 seconds of inactivity
  useEffect(() => {
    if (showVolumeSlider && !isInteracting) {
      const timer = setTimeout(() => setShowVolumeSlider(false), 2500);
      return () => clearTimeout(timer);
    }
  }, [showVolumeSlider, isInteracting]);

    useEffect(() => {
      fetch('/commit-id.txt')
        .then((res) => res.text())
        .then((text) => setAppBuildId(text.trim()))
        .catch(() => setAppBuildId('Unknown'));
    }, []);

  // Fast-forward on click: complete the dialogue immediately if not fully displayed
  const handleDialogueClick = () => {
    if (!dialogueClicked) {
      setDialogueClicked(true);
      if (!revealCompleted) {
        ttsService.current.stop(); 
        setCurrentWordIndex(words.length);
      } else {
        nextDialogue();
      }
    } else if (revealCompleted) {
      nextDialogue();
    }
  };

  const handleStartStory = () => {
    setShowIntro(false);
    setCurrentWordIndex(0);
    AnalyticsService.trackEvent('Playback', 'Started Story', storyId);
  };

  const showSnackbar = (message) => {
    setSnackbar({
      open: true,
      message,
      key: new Date().getTime(), // ✅ Use timestamp to force reset
    });
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleFeedbackClick = () => {
    const appVersion = appBuildId; // Ideally, get this dynamically
    const deviceInfo = navigator.userAgent;
    const storyTitle = storyId; // Pass this dynamically if possible
    const email = "coffeecatstudios2@gmail.com";

    const subject = encodeURIComponent("Feedback for StoryApp");
    const body = encodeURIComponent(
      `Hi Team,\n\nI’d like to share my feedback:\n\n---\nApp Version: ${appVersion}\nDevice Info: ${deviceInfo}\nStory Title: ${storyTitle}\n\nFeedback:\n`
    );

    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
    setSnackbar({
      open: true,
      message: 'Opening Email Client. If facing problems please email us ',
      key: new Date().getTime(),
      action: (
        <Button
          color="primary"
          size="small"
          onClick={() => {
            navigator.clipboard.writeText(email);
            setSnackbar({ ...snackbar, open: false }); // Close Snackbar
            showSnackbar('Email copied to clipboard.');
          }}
        >
          Copy Email
        </Button>
      ),
    });
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="flex-end"
      alignItems="center"
      sx={{
        backgroundColor: '#87CEEB', // Light blue background (fallback)
        backgroundImage: assets.backgroundImage ? `url(${assets.backgroundImage})` : 'none', // Use BG image from assets
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        p: 4,
        boxSizing: 'border-box', // Add this
        height: '100dvh', // ✅ Set height to 100vh
      }}
    >

      <Snackbar
        key={snackbar.key} // ✅ Reset timer on new message
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        message={snackbar.message}
        action={snackbar.action} // ✅ Adds the "Enable" button to the Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          top: 70,
          '& .MuiSnackbar-root': { display: 'flex', justifyContent: 'center' }, // Ensures it's centered
          '& .MuiSnackbarContent-root': {
            textAlign: 'center', // ✅ Centers text inside the Snackbar
            justifyContent: 'center', // ✅ Ensures button/action are also centered
            width: 'fit-content', // ✅ Prevents unnecessary wide Snackbar
            mx: 'auto' // ✅ Centers the Snackbar itself
          },
        }}
      >
      </Snackbar>

      <Box
        sx={{
          position: 'absolute',
          top: 16,
          left: 16, // ✅ Back button on the left side
          zIndex: 1000,
          backgroundColor: 'rgba(0, 0, 0, 0.7)', // Transparent background
          borderRadius: 4, // Rounded corners
        }}
      >
        <IconButton
          onClick={() => {
            onBackPress();
            AnalyticsService.trackEvent('Playback', 'Clicked', 'Home');
          }}
          sx={{
            color: '#1976d2'
          }}
        >
          <ArrowBack />
        </IconButton>

        <IconButton
          onClick={() => {
            handleFeedbackClick();
            AnalyticsService.trackEvent('Playback', 'Clicked', 'Feedback');
          }}
          sx={{
            color: '#1976d2'
          }}
        >
          <Feedback  />
        </IconButton>

        {/*
        <IconButton
          onClick={() => {
            setIsPaused(prev => !prev);
            showSnackbar(`Speech ${isPaused ? 'resumed' : 'paused'}`);
          }}
          sx={{
            color: isPaused ? 'red' : '#1976d2',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
          }}
        >
          {isPaused ? <PlayArrow /> : <Pause />}
        </IconButton>
        */}

      </Box>

      <Tooltip
        title="Control Speech, Autoplay, and Music here!"
        open={showTooltip} // Automatically opens when story starts
        placement="bottom" // Tooltip appears below the options bar
        arrow
      >
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            right: 20, // ✅ Options on the right side
            zIndex: 1000,
            display: 'flex',
            gap: 1, // Spacing between icons
            backgroundColor: 'rgba(0, 0, 0, 0.7)', // Transparent background
            borderRadius: 4, // Rounded corners
            opacity: showOptions ? 1 : 0,
            transform: showOptions ? 'scale(1)' : 'scale(0.5)', // Subtle scale effect
            transition: 'opacity 0.5s ease, transform 0.5s ease',
          }}
        >
          {/* TTS Toggle */}
          <Tooltip title={useTTS ? 'Disable Voice Dialogue' : 'Enable Voice Dialogue (Experimental feature)'}>
            <IconButton
              onClick={() => {

                if (!ttsService.current.isSupported()) {
                  showSnackbar('Voice dialogue is not supported on this browser'); // Show Snackbar             
                  AnalyticsService.trackEvent('Playback', 'TTS', 'unsupported');
                  return;
                }

                if (!useTTS && useTTSWarning) {
                  setUseTTSWarning(false);

                  // Show Snackbar instead of opening a Dialog
                  setSnackbar({
                    open: true,
                    message: 'Enable Voice Dialogue? (Experimental feature)',
                    key: new Date().getTime(),
                    action: (
                      <Button
                        color="primary"
                        size="small"
                        onClick={() => {
                          updateTTS(true);
                          setSnackbar({ ...snackbar, open: false }); // Close Snackbar
                          showSnackbar('Voice dialogue is Enabled.');
                        }}
                      >
                        Enable
                      </Button>
                    ),
                  });

                }
                else {
                  updateTTS(!useTTS);
                  showSnackbar(`Voice dialogue is ${useTTS ? 'Disabled' : 'Enabled. (Experimental feature)'}`);
                }

              }}
              sx={{
                color: useTTS ? '#1976d2' : 'gray',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
              }}
            >
              <RecordVoiceOver />
            </IconButton>
          </Tooltip>

          {/* Autoplay Toggle */}
          <Tooltip title={autoplay ? 'Disable Autoplay' : 'Enable Autoplay'}>
            <IconButton
              onClick={() => {
                updateAutoplay(!autoplay);
                showSnackbar(`Autoplay ${autoplay ? 'Disabled' : 'Enabled'}`);
              }}
              sx={{
                color: autoplay ? '#1976d2' : 'gray',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
              }}
            >
              <AutoMode />
            </IconButton>
          </Tooltip>

          {/* BGM Volume */}
          <Tooltip title={'Adjust Music Volume'}>
            <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
              {/* Volume Button */}
              <IconButton
                onClick={() => {
                  setShowVolumeSlider(true)
                  AnalyticsService.trackEvent("Playback", "BGM", "Clicked");
                }
                }
                sx={{
                  color: bgmVolume > 0 ? '#1976d2' : 'gray',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                }}
              >
                {bgmVolume > 0 ? <VolumeUp /> : <VolumeMute />}
              </IconButton>

              {/* Smoothly Appearing Volume Slider */}
              <Box
                sx={{
                  width: showVolumeSlider ? '120px' : '0px', // Expands when active
                  opacity: showVolumeSlider ? 1 : 0, // Fades in/out
                  transition: 'width 0.3s ease, opacity 0.3s ease', // Smooth animation
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  pr: showVolumeSlider ? 1.5 : 0, // Right padding
                }}
                onMouseDown={() => setIsInteracting(true)}  // Desktop click
                onMouseUp={() => setIsInteracting(false)}   // Desktop release
                onTouchStart={() => setIsInteracting(true)} // Mobile touch
                onTouchEnd={() => setIsInteracting(false)}  // Mobile release
              >
                <Slider
                  min={0}
                  max={1}
                  step={0.05}
                  value={bgmVolume}
                  onChange={(e, newValue) => handleBgmVolumeChange(newValue)}
                  sx={{
                    color: '#1976d2',
                    ml: 1,
                    height: 4, // Smaller height for the track
                    '& .MuiSlider-thumb': {
                      width: 10, // Smaller thumb
                      height: 10,
                      boxShadow: 'none', // Remove default shadow
                      // Remove ripple on hover, focus, and active states:
                      '&:hover, &.Mui-focusVisible, &.Mui-active': {
                        boxShadow: 'none',
                      },
                      // Also hide any pseudo-elements that might be showing the ripple effect
                      '&:before, &:after': {
                        display: 'none',
                      },
                    },
                    '& .MuiSlider-track': {
                      height: 4, // Thinner track
                    },
                    '& .MuiSlider-rail': {
                      height: 4, // Thinner rail
                    },
                  }}
                />
              </Box>
            </Box>
          </Tooltip>

        </Box>
      </Tooltip>

      {/* ✅ Intro Screen */}
      {showIntro && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'white',
            opacity: showIntro ? 1 : 0,
            transition: 'opacity 1s ease',
            pointerEvents: showIntro ? 'all' : 'none', // Disable interaction after fade-out
            zIndex: 10,
          }}
        >
          <Typography
            variant="h3"
            gutterBottom
            sx={{
              textAlign: 'center',
              mb: 2, // Adjusted margin to move up
            }}
          >
            {story.title}
          </Typography>

          {/* Play Button */}
          <IconButton
            onClick={handleStartStory}
            sx={{
              fontSize: '4rem',
              color: '#1976d2', // Blue color
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              '&:hover': {
                transform: 'scale(1.1)',
                transition: 'transform 0.2s ease',
              },
            }}
          >
            <PlayArrow fontSize="inherit" />
          </IconButton>

          {/* Prologue Box */}
          <Box
            sx={{
              mt: 3, // Space below play button
              px: 3,
              py: 2,
              maxWidth: { xs: '80%', sm: '30%' }, // 80% for mobile, 50% for desktop
              backgroundColor: 'rgba(0, 0, 0, 0.6)', // Semi-transparent background
              borderRadius: 2,
              textAlign: 'center',
            }}
          >
            {/* Prologue Heading */}
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white', mb: 1 }}>
              Prologue
            </Typography>
            {/* Prologue Text */}
            <Typography variant="body1" sx={{ fontSize: '0.9rem', color: 'white' }}>
              {story.prologue}
            </Typography>
          </Box>
        </Box>
      )}


      {/* Audio element for BG Music */}
      <audio ref={audioRef} />

      {/* Start Story Button and Checkboxes (Top Right) */}
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column', // Stack items vertically
          alignItems: 'flex-end', // Align items to the right
          gap: 1, // Add spacing between items
        }}
      >

      </Box>
      {!showIntro && !isEndDialogOpen && (
        <Box sx={{ position: 'relative', width: '100%', maxWidth: '600px', mb: 6 }}>
          {/* Dialogue Box */}
          <Box
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 1)',
              borderRadius: 2,
              p: 1.5,
              pb: 4,
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer', // Add pointer cursor to indicate clickability
              userSelect: 'none',
              transition: `opacity ${DIALOGUE_TRANSITION_DURATION}ms ease, transform ${DIALOGUE_TRANSITION_DURATION}ms ease`,
              opacity: isTransitioning ? 0 : 1,
              transform: isTransitioning ? 'translateY(20px)' : 'translateY(0)',
              pointerEvents: isTransitioning ? 'none' : 'auto',
            }}
            onClick={()=> {
                handleDialogueClick();
                AnalyticsService.trackEvent('Playback', 'DialogueClicked', 'Box');
              }} // Add click handler
          >
            <Avatar
              src={characterImage} // Use the character's image
              alt={character.name}
              sx={{
                width: 56,
                height: 56,
                mr: 2,
                backgroundColor: isNarratorDialogue ? "gray" : "transparent", // Gray for narrator
                color: "white",
                fontSize: "2rem",
                fontWeight: "bold",
                textAlign: "center",
              }}
            >
              {isNarratorDialogue ? "..." : (!characterImage && character.name[0])} {/* Fallback to first letter if no image */}
            </Avatar>
            <Box flexGrow={1}>
              <Typography variant="h6" color="white">
                {character.name}
              </Typography>
              <Typography variant="body1" color="white">
                {words.slice(0, currentWordIndex).map((word, index) => (
                  <span
                    key={index}
                    style={{
                      display: "inline-block",
                      opacity: 0,
                      animation: "fadeIn 0.3s ease-in-out forwards",
                      animationDelay: `${index * 50}ms`, // Staggered effect per word
                    }}
                    onAnimationEnd={index === words.length - 1 ? () => setRevealCompleted(true) : undefined}
                  >
                    {word}&nbsp;
                  </span>
                ))}
                {currentWordIndex < words.length}
              </Typography>
            </Box>
          </Box>
          {/* Next Icon Positioned Outside the Dialogue Box */}
          {revealCompleted && !autoplay && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation(); // prevent triggering dialogue click
                handleDialogueClick();
                AnalyticsService.trackEvent('Playback', 'DialogueClicked', 'Next');
              }}
              sx={{
                position: 'absolute',
                bottom: 0, // negative offset so it sits just below the dialogue box
                right: 0,
                color: 'white',
                transition: `opacity ${DIALOGUE_TRANSITION_DURATION}ms ease, transform ${DIALOGUE_TRANSITION_DURATION}ms ease`,
                opacity: isTransitioning ? 0 : 1,
                transform: isTransitioning ? 'translateY(20px)' : 'translateY(0)',
              }}
            >
              <Forward />
            </IconButton>
          )}
        </Box>
      )}

      {/* End Story Dialog */}
      <EndStoryDialog
        open={isEndDialogOpen}
        onReplay={() => {
          setCurrentIndex(0); // Restart the story
          setIsEndDialogOpen(false);
          setShowIntro(true);
          AnalyticsService.trackEvent('Playback', 'EndStoryDialog', 'Replay');
        }}
        onButtonMid={handleFeedbackClick}
        onGoBack={() => {
          onBackPress();
          AnalyticsService.trackEvent('Playback', 'EndStoryDialog', 'Home');
        }}
      />
    </Box>
  );
};

export default StoryPlayback;